import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Navigation, Pencil, Trash2 } from "lucide-react";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";
import Modal from "../Modal";
import ConfirmModal from "../ConfirmModal";
import PlaceInput from "../PlaceInput";
import BlurTextarea from "../BlurTextarea";

const empty = { name: "", address: "", lat: null, lng: null, place_id: null, address_source: null, notes: "" };

export default function SitesBlock({ customerId, sites, canEdit, onChanged, defaultOpen = true }) {
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(empty);
  // 2026-02-18 Bug #8 — edit goes through the full-page route now;
  // the legacy `editingSite` modal state has been removed.
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setForm(empty);
    setAddOpen(false);
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sites", {
        ...form,
        customer_id: customerId,
        lat: form.lat != null && form.lat !== "" ? Number(form.lat) : null,
        lng: form.lng != null && form.lng !== "" ? Number(form.lng) : null,
      });
      reset();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  // 2026-02-18 Bug #8 — Edit modal removed; saveEdit lives on the
  // full-page route (CustomerSiteEditPage.jsx).

  const del = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/sites/${confirmDel.id}`);
      setConfirmDel(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <FoldBlock
      icon={MapPin}
      iconColor="#1fb6c1"
      title="Sites"
      count={sites.length}
      defaultOpen={defaultOpen}
      canEdit={canEdit}
      onAdd={() => setAddOpen(true)}
      addLabel="Add site"
      testId="block-sites"
      emptyHint="No sites yet — add one so techs can navigate."
    >
      <div className="grid md:grid-cols-2 gap-2">
        {sites.map((s) => {
          const q = s.lat && s.lng ? `${s.lat},${s.lng}` : encodeURIComponent(s.address || "");
          const mapUrl = `https://www.google.com/maps/search/?api=1&query=${q}`;
          return (
            <div
              key={s.id}
              className="border border-white/5 rounded-md bg-white/[0.02] hover:border-[#1fb6c1]/30 transition-colors overflow-hidden"
              data-testid={`site-card-${s.id}`}
            >
              <div className="flex items-stretch">
                <Link
                  to={`/customers/${customerId}/sites/${s.id}`}
                  className="flex-1 min-w-0 p-3 hover:bg-white/[0.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1fb6c1]/40"
                  data-testid={`site-open-${s.id}`}
                  title="Open site detail"
                >
                  <div className="font-bold truncate">{s.name}</div>
                  <div className="text-xs text-[#a0aabf] flex items-start gap-1 mt-1 leading-snug">
                    <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                    <span className="break-words">{s.address}</span>
                  </div>
                  {s.notes && (
                    <div className="text-xs text-[#647087] mt-1.5 line-clamp-2">{s.notes}</div>
                  )}
                </Link>
                <div className="flex flex-col gap-1 shrink-0 p-3 border-l border-white/5">
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="hv-btn hv-btn-primary text-xs px-2 py-1"
                    data-testid={`site-navigate-${s.id}`}
                    title="Navigate"
                  >
                    <Navigation className="w-3 h-3" />
                  </a>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // 2026-02-18 Bug #8 — full-page edit, no modal.
                        navigate(`/customers/${customerId}/sites/${s.id}/edit`);
                      }}
                      className="hv-btn hv-btn-ghost text-xs px-2 py-1"
                      data-testid={`site-edit-${s.id}`}
                      title="Edit"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setConfirmDel(s); }}
                      className="hv-btn hv-btn-ghost text-xs px-2 py-1 text-rose-300"
                      data-testid={`site-delete-${s.id}`}
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add site modal */}
    </FoldBlock>
      <Modal open={addOpen} onClose={reset} title="New site" testId="site-add-modal">
        <form onSubmit={add} className="space-y-3" data-testid="site-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Site name *</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="hv-input mt-1"
              data-testid="site-input-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Address *</label>
            <PlaceInput
              value={form.address ? { address: form.address, lat: form.lat, lng: form.lng, place_id: form.place_id, source: form.address_source || "legacy" } : null}
              onChange={(v) => setForm((f) => ({
                ...f,
                address: v?.address || "",
                lat: v?.lat ?? null,
                lng: v?.lng ?? null,
                place_id: v?.place_id ?? null,
                address_source: v?.source ?? null,
              }))}
              testId="site-input-address"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
            <BlurTextarea
              value={form.notes}
              onSave={(v) => setForm((f) => ({ ...f, notes: v }))}
              testid="site-input-notes"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !form.address}
            className="hv-btn hv-btn-primary w-full"
            data-testid="site-submit"
          >
            {busy ? "Saving…" : "Add site"}
          </button>
        </form>
      </Modal>

      {/* 2026-02-18 Bug #8 — site edit now uses a full-page route
          (/customers/:cid/sites/:sid/edit) — modal removed. */}

      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={del}
        title="Delete site"
        message={
          <>
            Delete site <b>{confirmDel?.name}</b>? Job cards / equipment linked to it
            will lose their location reference.
          </>
        }
        confirmLabel="Delete site"
        testId="site-delete-confirm"
        busy={busy}
      />
    </>
  );
}
